/* eslint-disable react/prop-types */
import { forwardRef, useEffect, useRef, useState, useCallback, useImperativeHandle } from "react"

import * as React from "react"
import _ from "underscore"
import Downshift from "downshift"
import axios from "axios"
import Capem from "components/admin/capem"
import PropTypes from "prop-types"
import { Box, Spinner } from "@planningcenter/tapestry-react"
import { unescape } from "utils/helpers"

// onSelect: callback function that runs when the person selects an item from the search results
// disabledItemCondition: function that filters out a search result item (e.g current person, existing managers, etc).
//        Return true for an item that you want to be displayed, but to appear disabled.
// filteredItemCondition: function that filters out a search result item (e.g current person, existing managers, etc).
//        Return true for an item if you want it filtered out
// initialValue: If you want to set the search input to a default value (e.g from a URL param) that will fire off the
//        search as if the person typed in that query
// rootProps, labelProps, inputProps, menuProps, itemProps
// showNewPersonButton: [default true] boolean to show the 'Create a new person' button
// showSpinner: [default false] boolean to show a spinner in the input
// showLabel: [default false] show the Downshift-js label
// RootDOMRenderer, ItemRenderer, DisableditemRenderer: allow overriding components for the components that build up AccountCenterSearch

const AccountCenterSearch = forwardRef(
  (
    {
      onSelect = () => {},
      disabledItemCondition = _item => {
        return false
      },
      filteredItemCondition = _item => {
        return false
      },
      rootProps,
      inputProps,
      menuProps,
      itemProps,
      initialValue,
      showNewPersonButton = true,
      showSpinner = false,
      RootDOMRenderer = AccountCenterSearchRootDOM,
      ItemRenderer = AccountCenterSearchResult,
      DisableditemRenderer = ItemRenderer
    },
    ref
  ) => {
    const [q, setQ] = useState(null)
    const throttledSetQ = useRef(null)
    if (!throttledSetQ.current) {
      throttledSetQ.current = _.throttle(setQ, 300)
    }

    const handleInputValueChange = (inputValue, state) => {
      if (!state.selectedItem) {
        throttledSetQ.current(inputValue)
      }
    }

    const downshift = useRef(null)
    useEffect(() => {
      if (initialValue) {
        downshift.current.internalSetState({
          inputValue: initialValue,
          isOpen: true
        })
      }
    }, [initialValue])

    useImperativeHandle(ref, () => ({
      clearSelection() {
        downshift.current.clearSelection()
      }
    }))

    const handleNewPersonSave = (person, downshiftState) => {
      onSelect(person, downshiftState)
    }

    return (
      <Downshift
        onInputValueChange={handleInputValueChange}
        onChange={onSelect}
        itemToString={item => (item ? unescape(item.name) : "")}
        ref={downshift}
      >
        {({
          getInputProps,
          getItemProps,
          getMenuProps,
          isOpen,
          getRootProps,
          inputValue,
          clearSelection,
          highlightedIndex
        }) => (
          <RootDOMRenderer
            {...getRootProps({
              className: "search",
              ...rootProps
            })}
          >
            <AccountCenterFetch q={q}>
              {({ loading, _error, data: { people = [] } = {} }) => (
                <>
                  <AccountCenterSearchInput
                    getInputProps={getInputProps}
                    showSpinner={showSpinner || loading}
                    {...inputProps}
                  />
                  <AccountCenterSearchMenu getMenuProps={getMenuProps} {...menuProps}>
                    {!isOpen
                      ? null
                      : [
                          ...people.map((item, index) => {
                            if (filteredItemCondition(item)) {
                              return null
                            }
                            if (disabledItemCondition(item)) {
                              return (
                                <DisableditemRenderer
                                  key={index}
                                  item={item}
                                  index={index}
                                  getItemProps={getItemProps}
                                  itemProps={itemProps}
                                />
                              )
                            }
                            return (
                              <ItemRenderer
                                key={item.account_center_id}
                                item={item}
                                index={index}
                                getItemProps={getItemProps}
                                itemProps={itemProps}
                                highlightedIndex={highlightedIndex}
                              />
                            )
                          }),
                          <AccountCenterSearchNoResults
                            key={"noResults"}
                            inputValue={inputValue}
                            people={people}
                          />,
                          showNewPersonButton && (
                            <AccountCenterNewPerson
                              key={"newPerson"}
                              inputValue={inputValue}
                              onSave={person => {
                                handleNewPersonSave(person, { clearSelection })
                              }}
                            />
                          )
                        ]}
                  </AccountCenterSearchMenu>
                </>
              )}
            </AccountCenterFetch>
          </RootDOMRenderer>
        )}
      </Downshift>
    )
  }
)

AccountCenterSearch.displayName = "AccountCenterSearch"

const AccountCenterSearchRootDOM = forwardRef(({ children, ...props }, _ref) => {
  return <div {...props}>{children}</div>
})

AccountCenterSearchRootDOM.propTypes = {
  children: PropTypes.node
}

AccountCenterSearchRootDOM.displayName = "AccountCenterSearchRootDOM"

const AccountCenterSearchInput = ({ getInputProps, showSpinner = false, ...inputProps }) => {
  return (
    <div className="p-r d-f">
      <input
        {...getInputProps({
          type: "text",
          autoFocus: true,
          ...inputProps
        })}
      />
      {showSpinner && (
        <Box position="absolute" zIndex={1} top="6px" right={2}>
          <Spinner size="xs" thickness={1} />
        </Box>
      )}
    </div>
  )
}

AccountCenterSearchInput.propTypes = {
  getInputProps: PropTypes.func.isRequired
}

const AccountCenterSearchMenu = ({ getMenuProps, children, ...menuProps }) => {
  return (
    <ul
      {...getMenuProps({
        className: "autocomplete-results p-a l-0 r-0",
        ...menuProps
      })}
    >
      {children}
    </ul>
  )
}

AccountCenterSearchMenu.propTypes = {
  children: PropTypes.node,
  getMenuProps: PropTypes.func.isRequired
}

const AccountCenterSearchResult = ({ getItemProps, item, index, itemProps, highlightedIndex }) => {
  const {
    first_name,
    last_name,
    name_suffix,
    membership,
    status,
    cropped_avatar_url,
    contact_data: { phone_numbers = [], email_addresses = [], addresses = [] } = {}
  } = item

  const primaryAddress = addresses.find(a => a.primary === true)
  const primaryPhoneNumber = phone_numbers.find(p => p.primary === true)
  const primaryEmailAddress = email_addresses.find(e => e.primary === true)

  return (
    <li
      {...getItemProps({
        index,
        item,
        className: `autocomplete-result ${highlightedIndex === index && "highlighted"}`,
        ...itemProps
      })}
    >
      <button className={`autocomplete-result__link ${status}`}>
        <div className="mr-1">
          <img
            src={cropped_avatar_url}
            size={5}
            className="w-5 h-5 circle"
            alt={`profile for ${unescape(first_name)} ${unescape(last_name)} ${unescape(
              name_suffix
            )}`}
          />
        </div>
        <div className="autocomplete-result__details-wrapper">
          <div className="f-1">
            <div className="autocomplete-result__name autocomplete-results__primary-content">
              {unescape(first_name)} {unescape(last_name)} {unescape(name_suffix)}{" "}
              {status == "inactive" && ` (Inactive)`}
            </div>
            <div className="autocomplete-result__details autocomplete-results__primary-content">
              {primaryPhoneNumber && <span>{primaryPhoneNumber.number}</span>}
              {primaryEmailAddress && <span>{primaryEmailAddress.address}</span>}
            </div>
          </div>
          <div className="autocomplete-result__details ta-r">
            {membership && (
              <div className="autocomplete-results__secondary-content">{membership}</div>
            )}
            {primaryAddress && (
              <div className="autocomplete-results__secondary-content">
                {primaryAddress.city}, {primaryAddress.state}
              </div>
            )}
          </div>
        </div>
      </button>
    </li>
  )
}

AccountCenterSearchResult.propTypes = {
  item: PropTypes.shape({
    account_center_id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    cropped_avatar_url: PropTypes.string.isRequired,
    contact_data: PropTypes.object.isRequired,
    membership: PropTypes.string,
    first_name: PropTypes.string
  }).isRequired,
  highlightedIndex: PropTypes.number,
  index: PropTypes.number.isRequired,
  getItemProps: PropTypes.func.isRequired
}

const AccountCenterSearchNoResults = ({ inputValue = "", people }) => {
  return people.length !== 0 || inputValue.trim().length === 0 ? null : (
    <li className="p-1 ta-c autcomplete-result">
      &quot;{inputValue}&quot; doesn&apos;t exist in any Planning Center products yet.
    </li>
  )
}

AccountCenterSearchNoResults.propTypes = {
  inputValue: PropTypes.string.isRequired,
  people: PropTypes.array.isRequired
}

const AccountCenterNewPerson = ({ inputValue = "", onSave = () => {} }) => {
  const [firstName, setFirstName] = useState(null)
  const [lastName, setLastName] = useState(null)

  useEffect(() => {
    setFirstName(
      inputValue
        .split(" ")
        .slice(0, -1)
        .join(" ")
    )

    setLastName(inputValue.split(" ").pop())
  }, [inputValue])

  const handleSave = data => {
    if (data.saved) {
      onSave(data.person)
    }
  }

  return inputValue.trim().length === 0 ? null : (
    <li className="p-1">
      <Capem onSave={handleSave} firstName={firstName} lastName={lastName}>
        Create a New Person: {inputValue}
      </Capem>
    </li>
  )
}

AccountCenterNewPerson.propTypes = {
  inputValue: PropTypes.string.isRequired,
  onSave: PropTypes.func.isRequired
}

const AccountCenterFetch = ({ children, q }) => {
  const [data, setData] = useState({ people: [] })
  const [result, setResult] = useState({ people: [] })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [searchedAt, setSearchedAt] = useState(null)

  const handleResult = useCallback((nonce, result) => {
    setResult({ nonce, people: result.data })
  }, [])

  useEffect(() => {
    const { nonce, people } = result
    if (nonce >= searchedAt) {
      setData({ people })
    }
  }, [searchedAt, result])

  useEffect(() => {
    const nonce = new Date().getTime()

    const fetchData = async () => {
      setLoading(true)
      setError(false)
      setSearchedAt(nonce)

      const result = await axios(`/accounts/people?q=${q}`)

      try {
        handleResult(nonce, result)
      } catch (_e) {
        setError(true)
      }
      setLoading(false)
    }

    if (q) {
      fetchData()
    } else {
      setSearchedAt(new Date().getTime())
      setData({ people: [] })
    }
  }, [handleResult, q])

  return children({ data, loading, error })
}

AccountCenterFetch.propTypes = {
  q: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.array]).isRequired
}

export default AccountCenterSearch
